import {
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	Button,
	Textarea,
} from "@/components/primitives";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";

interface FutureStateStepProps {
	/**
	 * The future state text
	 */
	futureState: string;
	/**
	 * Callback when future state changes
	 */
	onFutureStateChange: (value: string) => void;
	/**
	 * Callback when user clicks "Save your future"
	 */
	onContinue: () => void;
	/**
	 * Callback when user clicks "Save" (without continuing)
	 */
	onSave?: () => void;
	/**
	 * Whether the future state is being generated
	 */
	isLoading?: boolean;
	/**
	 * Optional subtitle
	 */
	subtitle?: string;
}

export function FutureStateStep({
	futureState,
	onFutureStateChange,
	onContinue,
	onSave,
	isLoading = false,
	subtitle,
}: FutureStateStepProps) {
	return (
		<>
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600">
						<Sparkles className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold">
							Does this align with how you like your future to be?
						</CardTitle>
						<CardDescription className="text-sm mt-1">
							{subtitle || "You can make edits as required"}
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-6">
				<div className="space-y-6">
					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-6 w-6 animate-spin mr-2 text-primary" />
							<span className="text-muted-foreground">
								Generating future state...
							</span>
						</div>
					) : (
						<>
							<div>
								<label className="text-sm font-semibold mb-2 block text-foreground">
									Your Ideal Future State
								</label>
								<Textarea
									value={futureState}
									onChange={(e) => onFutureStateChange(e.target.value)}
									placeholder="Describe your ideal future state..."
									className="min-h-[250px] text-base"
									disabled={isLoading}
								/>
							</div>
							<div className="flex justify-end gap-2 pt-2">
								{onSave && (
									<Button
										variant="outline"
										onClick={onSave}
										disabled={isLoading || !futureState.trim()}
									>
										Save
									</Button>
								)}
								<Button
									onClick={onContinue}
									disabled={isLoading || !futureState.trim()}
									className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
								>
									Save your future
									<ArrowRight className="h-4 w-4 ml-2" />
								</Button>
							</div>
						</>
					)}
				</div>
			</CardContent>
		</>
	);
}
